<template>
    <v-dialog
        v-model="dialog"
        width="auto" persistent
    >
        <v-card width="500" :loading="loadSurveyViewConfigPresetsLoading">
            <v-btn 
                icon color="black" style="position: absolute; top: 8px; right: 8px;"
                @click="dialog = false"
            >
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-card-title>Configuración de vistas</v-card-title>
            <v-card-subtitle>Configura las opciones de encuesta para cada vista.</v-card-subtitle>
            <v-card-text>
                <v-card class="d-flex flex-row pa-1" elevation="0" color="rgba(0,0,0,0.1)">
                    <v-btn 
                        width="50%" elevation="0" :text="tab != 'Home'" @click="tab = 'Home'" 
                        :color="tab === 'Home' ? 'white' : 'rgba(0,0,0,0.4)'"
                    >
                        <v-icon left>mdi-home</v-icon>
                        Home
                    </v-btn>
                    <v-btn 
                        width="50%" elevation="0" :text="tab != 'Email'" @click="tab = 'Email'"
                        :color="tab === 'Email' ? 'white' : 'rgba(0,0,0,0.4)'"
                    >
                        <v-icon left>mdi-email</v-icon>
                        Email
                    </v-btn>
                </v-card>
            </v-card-text>
            <v-card-text>
                <v-form ref="form">
                    <v-card class="pa-1" elevation="0" color="rgba(0,0,0,0)">
                        <div class="d-flex flex-row justify-space-between align-center" style="width: 100%;">
                            <p style="font-weight: 500; font-size: 1.1em; margin-top: 12px;">Habilitar Encuesta</p>
                            <v-switch v-model="enableSurvey" color="black"></v-switch>
                        </div>
                        <div class="d-flex flex-column">
                            <p style="font-weight: 500; font-size: 1.1em;">Seleccionar Encuesta</p>
                            <v-autocomplete 
                                :loading="fetchSurveysLoading"
                                outlined dense
                                placeholder="Selecciona una encuesta"
                                :items="surveyItems"
                                v-model="surveyConfig"
                                :rules="enableSurvey ? [v => !!v || 'Este campo es requerido']: undefined"
                            ></v-autocomplete>
                        </div>
                        <div
                            v-if="changesAreSaved" 
                            class="v-btn v-btn--block v-btn--is-elevated v-btn--has-bg theme--dark v-size--default success"
                        >
                            Cambios Guardados
                        </div>
                        <v-btn 
                            v-else
                            block dark @click="saveChanges()"
                            color="black"
                            :loading="saveChangesLoading"
                        >
                            Guardar Cambios
                        </v-btn>
                    </v-card>
                </v-form>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
    import SurveysProvider from '../../../../providers/SurveysProvider'
    import SurveyViewConfigProvider from '../../../../providers/SurveyViewConfigProvider'

    export default {
        name: "SurveyViewConfigsModal",
        props: {
            value: Boolean
        },
        data: () => ({
            tab: 'Home',
            form: {
                surveyViewConfig: {
                    home: {
                        enable: false,
                        survey: null
                    },
                    email: {
                        enable: false,
                        survey: null
                    }
                }
            },
            surveyItems: [],
            fetchSurveysLoading: false,
            saveChangesLoading: false,
            savedChanges: {},
            changesAreSaved: false,
            loadSurveyViewConfigPresetsLoading: false
        }),
        computed: {
            dialog: {
                get(){
                    if(this.value) this.fetchSurveys()
                    return this.value
                },
                set(value){
                    this.$emit('input', value)
                }
            },
            enableSurvey: {
                get(){
                    return this.form.surveyViewConfig[this.tab.toLocaleLowerCase()].enable
                },
                set(value){
                    this.form.surveyViewConfig[this.tab.toLocaleLowerCase()].enable = value
                    this.changesAreSaved = JSON.stringify(this.savedChanges) === JSON.stringify(this.form)
                }
            },
            surveyConfig: {
                get(){
                    return this.form.surveyViewConfig[this.tab.toLocaleLowerCase()].survey
                },
                set(value){
                    this.form.surveyViewConfig[this.tab.toLocaleLowerCase()].survey = value
                    this.changesAreSaved = JSON.stringify(this.savedChanges) === JSON.stringify(this.form)
                }
            }
        },
        async mounted(){
            await this.fetchSurveys()
            await this.loadSurveyViewConfigPresets()
        },
        methods: {
            async loadSurveyViewConfigPresets(){
                try {
                    this.loadSurveyViewConfigPresetsLoading = true
                    const { data: { fetchSurveyViewConfig } } = await SurveyViewConfigProvider.fetchSurveyViewConfig()
                    const homeConfig = fetchSurveyViewConfig.find(item => item.view === 'home')
                    const emailConfig = fetchSurveyViewConfig.find(item => item.view === 'email')
                    this.form.surveyViewConfig.home = {enable: homeConfig.enable, survey: homeConfig.survey.id}
                    this.form.surveyViewConfig.email = {enable: emailConfig.enable, survey: emailConfig.survey.id}
                } catch (error) {
                    this.errorAlert = {
                        enable: true,
                        message: 'Ups! Algo salió mal al intentar precargar los datos de configuración.'
                    }
                } finally {
                    this.loadSurveyViewConfigPresetsLoading = false
                }
            },
            async fetchSurveys(){
                try {
                    this.fetchSurveysLoading = true
                    const {data: {fetchSurvey}} = await SurveysProvider.fetchSurvey()
                    this.surveyItems = fetchSurvey.map(survey => ({
                        value: survey.id,
                        text: survey.name
                    }))
                } catch (error) {
                    this.errorAlert = {
                        enable: true,
                        message: 'Ups! Algo salió mal al intentar traer los datos de las encuestas.'
                    }
                } finally {
                    this.fetchSurveysLoading = false
                }
            },
            async saveChanges(){
                try {
                    if(!this.$refs.form.validate()) return
                    this.saveChangesLoading = true
                    const surveyViewConfig = [
                        {view: 'home', ...this.form.surveyViewConfig.home},
                        {view: 'email', ...this.form.surveyViewConfig.email}
                    ]
                    await SurveyViewConfigProvider.updateSurveyViewConfig(surveyViewConfig)
                    this.savedChanges = JSON.parse(JSON.stringify(this.form))
                    this.changesAreSaved = JSON.stringify(this.savedChanges) === JSON.stringify(this.form)
                } catch (error) {
                    this.errorAlert = {
                        enable: true,
                        message: 'Ups! Algo salió mal al intentar guardar los cambios.'
                    }
                } finally {
                    this.saveChangesLoading = false
                }
            }
        }
    }
</script>